import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaFileInvoice, FaPlus } from "react-icons/fa";
import { Stat } from "../../pages/dm/components/common";
import { Empty } from "../../pages/work/WorkUi";
import billingApi, { billingError } from "./billingApi";
import { InvoiceStatus, fmtDate, money, useBillingMeta } from "./BillingUi";
import "../../pages/dm/dm.css";
import "../../pages/work/work.css";
import "./billing.css";

/** Client page > Invoices: what this client was billed, paid and still owes. */
function ClientInvoicesPanel({ clientId }) {
  const navigate = useNavigate();
  const { meta } = useBillingMeta();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    billingApi
      .clientStatement(clientId)
      .then((d) => alive && setData(d))
      .catch((err) => alive && setError(billingError(err, "Could not load invoices")));
    return () => {
      alive = false;
    };
  }, [clientId]);

  if (error) return <div className="dmp-error">{error}</div>;
  if (!data) return <div className="wk-muted">Loading…</div>;
  const cur = data.currency;

  return (
    <div className="dmp wk bl">
      <div className="dmp-stats">
        <Stat label="Billed" value={money(data.totals.billed, cur)} tone="blue" />
        <Stat label="Paid" value={money(data.totals.paid, cur)} tone="green" />
        <Stat label="Still owes" value={money(data.totals.balance, cur)} tone="amber" />
        <Stat label="Overdue" value={money(data.totals.overdue, cur)} tone="rose" />
      </div>
      <div className="dmp-card">
        <h3 className="dmp-card__title">
          Invoices
          {meta?.access?.manage ? (
            <Link className="dmp-btn dmp-btn--sm" to={`/portal/finance/client-invoices/new?client=${clientId}`}>
              <FaPlus aria-hidden="true" /> New invoice
            </Link>
          ) : null}
        </h3>
        {data.invoices.length ? (
          <table className="wk-table wk-proj-table bl-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Date</th>
                <th>Due</th>
                <th className="bl-num">Total</th>
                <th className="bl-num">Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((inv) => (
                <tr key={inv.Invoice_Id} className="wk-proj-row" onClick={() => navigate(`/portal/finance/client-invoices/${inv.Invoice_Id}`)}>
                  <td data-label="Invoice">
                    <Link className="wk-proj-name" to={`/portal/finance/client-invoices/${inv.Invoice_Id}`} onClick={(e) => e.stopPropagation()}>
                      {inv.Invoice_No || "Draft"}
                    </Link>
                    {inv.Project_Name || inv.Service_Name ? (
                      <div className="wk-muted" style={{ fontSize: 12 }}>
                        {inv.Project_Name || inv.Service_Name}
                      </div>
                    ) : null}
                  </td>
                  <td data-label="Date">{fmtDate(inv.Invoice_Date)}</td>
                  <td data-label="Due">
                    {fmtDate(inv.Due_Date)}
                    {inv.Is_Overdue ? <div className="bl-late">{inv.Days_Overdue} days late</div> : null}
                  </td>
                  <td data-label="Total" className="bl-num">{money(inv.Total_Amount, inv.Currency)}</td>
                  <td data-label="Balance" className="bl-num">
                    <strong>{money(inv.Balance_Amount, inv.Currency)}</strong>
                  </td>
                  <td data-label="Status">
                    <InvoiceStatus status={inv.Display_Status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty icon={FaFileInvoice} title="No invoices for this client yet" />
        )}
      </div>
      {data.payments.length ? (
        <div className="dmp-card">
          <h3 className="dmp-card__title">Payments Received</h3>
          <ul className="bl-payments">
            {data.payments.slice(0, 20).map((p) => (
              <li key={p.Payment_Id}>
                <div>
                  <strong>{money(p.Amount, cur)}</strong> · {fmtDate(p.Payment_Date)} · {p.Invoice_No}
                  <div className="wk-muted" style={{ fontSize: 12 }}>
                    {[p.Payment_Mode, p.Reference].filter(Boolean).join(" · ") || "No details"}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default ClientInvoicesPanel;
